import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { fromEvent, Subject, timer } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { Direction, Position } from '../models/snake';

@Component({
    selector: 'app-snake',
    templateUrl: './snake.component.html',
    styleUrls: ['./snake.component.css']
})
export class SnakeComponent implements OnInit, AfterViewInit, OnDestroy {

    private snakeStartPos: Position[] = [{ x: 7, y: 4 }, { x: 6, y: 4 }, { x: 5, y: 4 }];


    @ViewChild('canvas', { read: ElementRef, static: false }) private canvasRef: ElementRef;
    private canvas: any;
    private ctx: CanvasRenderingContext2D;
    private refreshRate = 200;
    private direction = Direction.RIGHT;
    // private nextDirection = Direction.RIGHT;
    private tileSize = 10;
    private snakePosition: Position[];
    private food: Position;
    private gameRunning = true;
    private ngUnsubscribe: Subject<any> = new Subject();
    private timerSubject: Subject<number> = new Subject();
    private inputBuffer: Direction[] = [];

    public score = 0;

    constructor() {

    }

    ngOnInit(): void {

    }

    ngAfterViewInit(): void {
        this.canvas = this.canvasRef.nativeElement;
        this.ctx = this.canvas.getContext('2d');
        this.start();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public start() {
        this.snakePosition = this.snakeStartPos;
        this.drawSnake();
        this.createFood();
        this.drawFood();

        fromEvent(document, 'keydown').pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: any) => this.onKeyDown(event));

        this.timerSubject.pipe(
            takeUntil(this.ngUnsubscribe),
            filter(() => this.gameRunning),
            switchMap((t: number) => timer(0, t))).pipe(
                takeUntil(this.ngUnsubscribe),
                filter(() => this.gameRunning)).subscribe(() => {
                    this.run();
                });

        this.timerSubject.next(this.refreshRate);
    }

    @HostListener('keydown')
    private onKeyDown(event: KeyboardEvent) {
        let nextDirection: Direction = Direction.INVALID;
        let prevDirection: Direction;
        if (this.inputBuffer.length > 0) {
            prevDirection = this.inputBuffer[this.inputBuffer.length - 1];
        } else {
            prevDirection = this.direction;
        }
        switch (event.key) {
            case 'ArrowDown':
                if (prevDirection !== Direction.UP) {
                    nextDirection = Direction.DOWN;
                }
                break;
            case 'ArrowUp':
                if (prevDirection !== Direction.DOWN) {
                    nextDirection = Direction.UP;
                }
                break;
            case 'ArrowLeft':
                if (prevDirection !== Direction.RIGHT) {
                    nextDirection = Direction.LEFT;
                }
                break;
            case 'ArrowRight':
                if (prevDirection !== Direction.LEFT) {
                    nextDirection = Direction.RIGHT;
                }
                break;
            default:
                return;
        }

        if (nextDirection !== Direction.INVALID && this.inputBuffer.length < 2) {
            this.inputBuffer.push(nextDirection);
        }

    }

    private drawSnake() {

        for (const pos of this.snakePosition) {
            this.ctx.beginPath();
            this.ctx.fillStyle = '#000000';
            this.ctx.fillRect(pos.x * this.tileSize, pos.y * this.tileSize, this.tileSize, this.tileSize);
            this.ctx.closePath();
        }

    }

    private drawFood() {
        this.ctx.fillStyle = '#FF0000';
        this.ctx.fillRect(this.food.x * this.tileSize, this.food.y * this.tileSize, this.tileSize, this.tileSize);
    }

    private drawScore() {
        this.ctx.font = '20px san-serif';
        this.ctx.fillText('Score: ' + this.score, 400, 50);
    }

    private createFood() {
        let foodPos: Position;

        do {
            foodPos = {
                x: Math.round(Math.random() * (this.canvas.width / this.tileSize - 1)),
                y: Math.round(Math.random() * (this.canvas.height / this.tileSize) - 1)
            };
        } while (this.isSnakePosition(foodPos));
        this.food = foodPos;
    }

    private isSnakePosition(pos: Position): boolean {
        const found = this.snakePosition.find((p) => {
            return pos.x === p.x && pos.y === p.y;
        });
        return !!found;
    }

    private move(hasFood: boolean) {

        const newHeadPos: Position = { x: this.snakePosition[0].x, y: this.snakePosition[0].y };
        if (this.inputBuffer.length > 0) {
            this.direction = this.inputBuffer.shift();
        }

        switch (this.direction) {
            case Direction.DOWN:
                newHeadPos.y += 1;
                break;
            case Direction.UP:
                newHeadPos.y -= 1;
                break;
            case Direction.LEFT:
                newHeadPos.x -= 1;
                break;
            case Direction.RIGHT:
                newHeadPos.x += 1;
                break;
        }
        this.snakePosition.unshift(newHeadPos);
        if (!hasFood) {
            this.snakePosition.pop();
        }

        if (this.hitSelfOrWall(newHeadPos)) {
            this.showGameOver();
        }
    }

    private hitSelfOrWall(headPos: Position): boolean {
        const absPos: Position = {
            x: headPos.x * this.tileSize,
            y: headPos.y * this.tileSize
        };
        const hitWall = absPos.x < 0 || absPos.x >= this.canvas.width || absPos.y < 0 || absPos.y >= this.canvas.height;

        const hitSnake = this.snakePosition.slice(1).find((p) => {
            return headPos.x === p.x && headPos.y === p.y;
        });
        return hitWall || !!hitSnake;
    }

    private showGameOver() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.font = '40px san-serif';
        this.ctx.fillText('Game Over', 250, 250);
        this.gameRunning = false;
    }

    private isEatingFood(): boolean {
        const found = this.snakePosition.find((p) => {
            return this.food.x === p.x && this.food.y === p.y;
        });
        return !!found;
    }

    private run() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        const eating = this.isEatingFood();

        this.move(eating);
        this.drawFood();
        this.drawSnake();
        if (eating) {
            this.createFood();
            this.drawFood();
            this.score += 5;
            this.refreshRate = this.refreshRate * 0.9;
            this.timerSubject.next(this.refreshRate);

        }
        this.drawScore();

    }



}
